import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  inject,
} from '@angular/core';
import { GalaxyResizeAppearance, GalaxyResizeDirection } from './interface';

// If/when we want to add touchevent support for mobile,
// we can use code from angular-split
// https://github.com/angular-split/angular-split/blob/main/projects/angular-split/src/lib/component/split.component.ts

@Component({
  selector: 'glxy-resize',
  templateUrl: './resize.component.html',
  styleUrls: ['./resize.component.scss'],
})
export class ResizeComponent implements OnInit, OnDestroy {
  @HostBinding('class.glxy-resize') public true = 1;

  @Input() set width(width: number | string) {
    this.widthProperty = this.handleWidthInput(width);
  }
  @Input() set minWidth(width: number | string) {
    this.minWidthProperty = this.handleWidthInput(width);
  }
  @Input() set maxWidth(width: number | string) {
    this.maxWidthProperty = this.handleWidthInput(width);
  }
  @Input() appearance: GalaxyResizeAppearance = 'standard';
  @Input() direction: GalaxyResizeDirection = 'right';
  @Input() overflow: 'scroll' | 'hidden' | 'auto' | 'visible' = 'auto';

  @Output() dragStarted = new EventEmitter<null>();

  @Output() dragStopped = new EventEmitter<null>();

  @HostBinding('class.is-dragging') isDragging = false;
  @HostBinding('style.width') widthProperty?: string;
  @HostBinding('style.min-width') minWidthProperty?: string;
  @HostBinding('style.max-width') maxWidthProperty?: string;
  @HostBinding('style.overflow') overflowProperty?: string;

  // Apperance Host Bindings
  @HostBinding('class.glxy-resize-appearance-large')
  get isLargeAppearance(): boolean {
    return this.appearance === 'large';
  }
  @HostBinding('class.glxy-resize-appearance-standard')
  get isStandardAppearance(): boolean {
    return this.appearance === 'standard';
  }
  @HostBinding('class.glxy-resize-appearance-simple')
  get isSimpleAppearance(): boolean {
    return this.appearance === 'simple';
  }

  // Direction Host Bindings
  @HostBinding('class.glxy-resize-direction-left')
  get isDirectionLeft(): boolean {
    return this.direction === 'left';
  }
  @HostBinding('class.glxy-resize-direction-right')
  get isDirectionRight(): boolean {
    return this.direction === 'right';
  }

  private initialParentOffsetWidth = 0;
  private initialclientX = 0;
  private canResizeWidth = false;

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  ngOnInit(): void {
    this.renderer.listen('document', 'mouseup', this.dragStop.bind(this));
  }

  ngOnDestroy(): void {
    this.renderer.destroy();
  }

  dragStart(event: MouseEvent): void {
    event.preventDefault();
    this.initialParentOffsetWidth = this.el.nativeElement.offsetWidth;
    this.initialclientX = event.clientX;
    this.canResizeWidth = true;
    this.isDragging = true;
    this.dragStarted.emit();
    document.querySelector('body')?.classList.add('has-element-dragging');
  }

  dragStop(): void {
    if (this.isDragging) {
      this.canResizeWidth = false;
      this.isDragging = false;
      this.dragStopped.emit();
      document.querySelector('body')?.classList.remove('has-element-dragging');
    }
  }

  @HostListener('window:mousemove', ['$event']) resize(event: MouseEvent): void {
    if (this.canResizeWidth) {
      event.preventDefault();
      const moveDiff = event.clientX - this.initialclientX;
      let finalWidth: number;

      if (this.direction === 'right') {
        finalWidth = this.initialParentOffsetWidth + moveDiff;
      } else {
        finalWidth = this.initialParentOffsetWidth - moveDiff;
      }

      this.width = finalWidth;
    }
  }

  private handleWidthInput(width: string | number): string {
    if (typeof width === 'number') {
      return `${width}px`;
    }
    return width;
  }
}
